import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { GAMES, CASINO_CATEGORIES, CASINO_SECTIONS } from '../mock/data';
import { useDemo } from '../context/DemoContext';
import { Reveal } from '../components/xc/Bits';
import { InvestorNote } from '../components/xc/InvestorNote';
import * as Icons from 'lucide-react';
import { Search, Play, X, Users, Wallet } from 'lucide-react';

function GameTile({ game, onPlay }) {
  const Icon = Icons[game.icon] || Icons.Dice5;
  return (
    <button onClick={onPlay} className="group relative rounded-xl overflow-hidden text-left w-full aspect-[3/4] xc-card">
      <div className="absolute inset-0" style={{ background: game.grad }} />
      <div className="absolute inset-0 xc-grid opacity-20" />
      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/10 to-transparent" />
      <div className="absolute top-3 left-3"><span className="text-[10px] font-600 tracking-[0.14em] uppercase px-2 py-1 rounded-full bg-black/40 text-white/90 border border-white/15">{game.tag}</span></div>
      <div className="absolute inset-0 grid place-items-center"><Icon className="h-10 w-10 text-white/85 drop-shadow-lg group-hover:scale-110 transition-transform duration-500" /></div>
      <div className="absolute bottom-0 left-0 right-0 p-4">
        <div className="text-[10px] text-white/50 uppercase tracking-wide">Provider Game — Demo</div>
        <div className="font-display font-600 text-white leading-tight">{game.title}</div>
      </div>
      <div className="absolute inset-0 grid place-items-center opacity-0 group-hover:opacity-100 transition-opacity bg-black/40">
        <span className="btn-neon rounded-full h-12 w-12 grid place-items-center"><Play className="h-5 w-5 fill-white" /></span>
      </div>
    </button>
  );
}

export default function Casino() {
  const nav = useNavigate();
  const { missionInviteDismissed, dismissInvite } = useDemo();
  const [cat, setCat] = useState('All');
  const [q, setQ] = useState('');

  const filtered = useMemo(() => GAMES.filter((g) =>
    (cat === 'All' || g.cat.includes(cat)) && g.title.toLowerCase().includes(q.toLowerCase())
  ), [cat, q]);

  const play = () => nav('/game/provider-demo');
  const featured = GAMES[0];
  const searching = q.length > 0 || cat !== 'All';

  return (
    <div className="bg-[#070609] min-h-screen">
      {/* Featured banner */}
      <section className="relative overflow-hidden border-b border-white/5">
        <div className="absolute inset-0" style={{ background: 'linear-gradient(120deg,#1a0a2e,#2a0f1e)' }} />
        <div className="absolute inset-0 xc-grid opacity-20" />
        <div className="relative max-w-[1440px] mx-auto px-4 lg:px-8 py-10 flex flex-col md:flex-row items-center justify-between gap-6">
          <div>
            <div className="text-xs tracking-[0.24em] uppercase text-[#FF204E] mb-2">Casino Lobby</div>
            <h1 className="font-display font-700 text-3xl md:text-4xl text-white">Just play. No strings attached.</h1>
            <p className="mt-2 text-white/55 max-w-lg text-sm">A normal premium online casino. Ignore Xincity entirely if you like — your progress is always here when you return.</p>
            <div className="mt-3"><InvestorNote id="casino-lobby" title="Why the lobby stays classic">
              The lobby intentionally mirrors a familiar tier-1 casino layout. Players convert best in patterns they already trust — the Xincity layer earns attention gradually instead of demanding it on day one.
            </InvestorNote></div>
            <div className="mt-4 flex items-center gap-2 text-sm text-white/60"><Wallet className="h-4 w-4 text-[#E0B85A]" /> Casino Demo Balance: <span className="text-white font-600">€183.40</span></div>
          </div>
          <button onClick={play} className="btn-neon rounded-xl px-6 py-4 font-600 flex items-center gap-2 flex-shrink-0"><Play className="h-5 w-5 fill-white" /> Play {featured.title}</button>
        </div>
      </section>

      <section className="max-w-[1440px] mx-auto px-4 lg:px-8 py-8">
        {/* Search + filters */}
        <div className="flex flex-col md:flex-row gap-4 md:items-center justify-between mb-6">
          <div className="relative w-full md:max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-white/40" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search games..."
              className="w-full bg-white/[0.04] border border-white/10 rounded-lg pl-10 pr-4 py-2.5 text-sm text-white placeholder:text-white/35 focus:border-[#B026FF]/50 outline-none transition-colors" />
          </div>
          <div className="flex flex-wrap gap-2">
            {CASINO_CATEGORIES.map((c) => (
              <button key={c} onClick={() => setCat(c)}
                className={`px-4 py-2 rounded-lg text-sm font-500 transition-colors border ${cat === c ? 'text-white border-[#FF204E]/50 bg-[#FF204E]/10' : 'text-white/55 border-white/10 hover:text-white'}`}>{c}</button>
            ))}
          </div>
        </div>

        {/* Mission invitation card */}
        {!missionInviteDismissed && (
          <div className="mb-8 rounded-xl overflow-hidden border border-[#B026FF]/25 xc-glass">
            <div className="p-4 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-lg grid place-items-center flex-shrink-0" style={{ background: '#B026FF18', border: '1px solid #B026FF44' }}><Users className="h-5 w-5 text-[#B026FF]" /></div>
                <div>
                  <div className="text-sm text-white/90 font-500">Your crew is assembling for <span className="neon-purple">The Loyalty Job</span></div>
                  <div className="text-xs text-white/45 mt-0.5">3 / 4 ready — optional, no pressure</div>
                </div>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                <button onClick={() => nav('/missions/loyalty-job')} className="btn-neon rounded-lg px-5 py-2 text-sm font-600">JOIN</button>
                <button onClick={dismissInvite} className="btn-ghost rounded-lg px-4 py-2 text-sm font-500 flex items-center gap-1"><X className="h-3.5 w-3.5" /> NOT NOW</button>
              </div>
            </div>
          </div>
        )}

        {searching ? (
          <div>
            <h2 className="font-display font-600 text-lg text-white mb-4">{filtered.length} game{filtered.length !== 1 ? 's' : ''}</h2>
            {filtered.length === 0 ? (
              <div className="text-center py-20 text-white/40">No games found. Try a different filter.</div>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
                {filtered.map((g) => <GameTile key={g.id} game={g} onPlay={play} />)}
              </div>
            )}
          </div>
        ) : (
          CASINO_SECTIONS.map((section) => {
            const items = GAMES.filter((g) => g.section === section);
            if (!items.length) return null;
            return (
              <div key={section} className="mb-10">
                <h2 className="font-display font-600 text-lg text-white mb-4 flex items-center gap-2"><span className="h-4 w-1 rounded-full" style={{ background: 'linear-gradient(#FF204E,#B026FF)' }} />{section}</h2>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
                  {items.map((g) => <Reveal key={g.id}><GameTile game={g} onPlay={play} /></Reveal>)}
                </div>
              </div>
            );
          })
        )}
      </section>
    </div>
  );
}
